/**
 * Verkochte woningen als referentie voor een taxatie (MA-56).
 *
 * Een transactie is iets anders dan een listing: een listing is het aanbod van
 * nu en wordt bij elke haal overschreven, een transactie is een verkoop die
 * blijft. Ze komen uit een RealWorks-uitdraai van verkochte woningen (zie
 * docs/taxatie.md). Zolang er geen koopsommen zijn, kan dezelfde import ook
 * woningen die verkocht onder voorbehoud of onder bod staan inlezen; die dragen
 * dan hun laatste vraagprijs en de prijssoort zegt dat.
 */
import { z } from 'zod';
import { buitenruimteSchema, prijssoortSchema } from './taxatie';

const geheel = z.number().int();

/** Wat op een pilletje en in de kandidatenlijst staat. */
export const transactiePuntSchema = z.object({
  id: z.uuid(),
  adres: z.string(),
  postcode: z.string().nullable(),
  prijs: geheel,
  prijssoort: prijssoortSchema,
  /** Koopdatum of datum van de status, yyyy-mm-dd. */
  datum: z.string().nullable(),
  oppervlakteM2: geheel.nullable(),
  prijsPerM2: geheel.nullable(),
  kamers: geheel.nullable(),
  slaapkamers: geheel.nullable(),
  soortWoning: z.string().nullable(),
  buitenruimte: buitenruimteSchema.nullable(),
  bouwjaar: geheel.nullable(),
  energielabel: z.string().nullable(),
  aantalFotos: geheel,
  lat: z.number(),
  lng: z.number(),
  /** Hemelsbreed tot het doelpand, als er een gevraagd is. */
  afstandM: geheel.nullable(),
});
export type TransactiePunt = z.infer<typeof transactiePuntSchema>;

export const transactiePuntenSchema = z.array(transactiePuntSchema);

/** Het detail: alles van het pilletje plus wat je in het vergelijkingsscherm leest. */
export const transactieDetailSchema = transactiePuntSchema.extend({
  plaats: z.string().nullable(),
  bron: z.string(),
  makelaar: z.string().nullable(),
  vraagprijs: geheel.nullable(),
  transactiedatum: z.string().nullable(),
  transportdatum: z.string().nullable(),
  dagenTeKoop: geheel.nullable(),
  prijsVerlaagd: z.boolean(),
  onderhoudBinnen: z.string().nullable(),
  onderhoudBuiten: z.string().nullable(),
  eigendom: z.string().nullable(),
  canon: z.number().nullable(),
  vveBijdrage: z.number().nullable(),
  woonlagen: geheel.nullable(),
  tekst: z.string().nullable(),
  /** Alle velden uit de bron, ook wat geen eigen kolom kreeg. */
  velden: z.record(z.string(), z.string()),
  fotos: z.array(z.object({ volgnummer: geheel, label: z.string().nullable() })),
});
export type TransactieDetail = z.infer<typeof transactieDetailSchema>;

/** Wat een foto laat zien; met de hand gezet, één klik per foto. */
export const FOTO_LABELS = [
  'gevel',
  'woonkamer',
  'keuken',
  'badkamer',
  'slaapkamer',
  'buitenruimte',
  'uitzicht',
  'plattegrond',
  'overig',
] as const;
export const fotoLabelSchema = z.enum(FOTO_LABELS);
export type FotoLabel = z.infer<typeof fotoLabelSchema>;

export const transactiesQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  /** Hoe ver om het doelpand heen; standaard anderhalve kilometer. */
  straalM: z.coerce.number().int().min(50).max(10000).default(1500),
});
export type TransactiesQuery = z.infer<typeof transactiesQuerySchema>;
